import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './components/AdminLayout';
import Login from './pages/Login';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Support from './pages/Support';
import PublicLanding from './pages/PublicLanding';

import OverviewPage from './pages/admin/OverviewPage';
import GrowthPage from './pages/admin/GrowthPage';
import RetentionPage from './pages/admin/RetentionPage';
import RevenuePage from './pages/admin/RevenuePage';
import PipelineHealthPage from './pages/admin/PipelineHealthPage';
import AlertsPage from './pages/admin/AlertsPage';
import CompatMatrixPage from './pages/admin/CompatMatrixPage';
import ErrorLeaderboardPage from './pages/admin/ErrorLeaderboardPage';
import OuiAnalysisPage from './pages/admin/OuiAnalysisPage';
import SinkRegistryPage from './pages/admin/SinkRegistryPage';
import UnknownOuiPage from './pages/admin/UnknownOuiPage';
import CompatDevicesPage from './pages/admin/CompatDevicesPage';
import CompatDeviceEditPage from './pages/admin/CompatDeviceEditPage';
import FeatureAdoptionPage from './pages/admin/FeatureAdoptionPage';
import GeoPage from './pages/admin/GeoPage';
import CastingAnalyticsPage from './pages/admin/CastingAnalyticsPage';
import SessionDetailPage from './pages/admin/SessionDetailPage';
import SiteVisitorsPage from './pages/admin/SiteVisitorsPage';
import DbConsolePage from './pages/admin/DbConsolePage';
import PipelineAnalyticsPage from './pages/admin/PipelineAnalyticsPage';
import ContentCurationPage from './pages/admin/ContentCurationPage';
import LiveEventsPage from './pages/admin/LiveEventsPage';
import SourceProtocolMatrixPage from './pages/admin/SourceProtocolMatrixPage';
import BaselinePage from './pages/admin/BaselinePage';
import ConnectionFunnelPage from './pages/admin/ConnectionFunnelPage';
import FeedbackInboxPage from './pages/admin/FeedbackInboxPage';

import './index.css';

function getSessionToken() {
  let sid = sessionStorage.getItem('zb_sid');
  if (!sid) {
    sid = Math.random().toString(36).substr(2) + Date.now().toString(36);
    sessionStorage.setItem('zb_sid', sid);
  }
  return sid;
}

function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    const apiBase = process.env.REACT_APP_API_URL || '/api';
    fetch(`${apiBase}/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: location.pathname, referrer: document.referrer, sessionToken: getSessionToken() }),
    }).catch(() => {});
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <PageTracker />
        <Routes>
          <Route path="/adminlogin" element={<Login />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/support" element={<Support />} />

          <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/admin/overview" replace />} />
            <Route path="overview" element={<OverviewPage />} />
            <Route path="growth" element={<GrowthPage />} />
            <Route path="retention" element={<RetentionPage />} />
            <Route path="revenue" element={<RevenuePage />} />
            <Route path="pipeline" element={<PipelineHealthPage />} />
            <Route path="alerts" element={<AlertsPage />} />
            <Route path="features" element={<FeatureAdoptionPage />} />
            <Route path="geo" element={<GeoPage />} />
            <Route path="compat/matrix" element={<CompatMatrixPage />} />
            <Route path="compat/errors" element={<ErrorLeaderboardPage />} />
            <Route path="compat/oui" element={<OuiAnalysisPage />} />
            <Route path="compat/sinks" element={<SinkRegistryPage />} />
            <Route path="compat/unknown-oui" element={<UnknownOuiPage />} />
            <Route path="compat/devices" element={<CompatDevicesPage />} />
            <Route path="compat/devices/:id" element={<CompatDeviceEditPage />} />
            <Route path="casting" element={<CastingAnalyticsPage />} />
            <Route path="session/:id" element={<SessionDetailPage />} />
            <Route path="visitors" element={<SiteVisitorsPage />} />
            <Route path="db-console" element={<DbConsolePage />} />
            <Route path="pipeline-analytics" element={<PipelineAnalyticsPage />} />
            <Route path="content" element={<ContentCurationPage />} />
            <Route path="live-events" element={<LiveEventsPage />} />
            <Route path="source-protocol-matrix" element={<SourceProtocolMatrixPage />} />
            <Route path="baseline" element={<BaselinePage />} />
            <Route path="connection-funnel" element={<ConnectionFunnelPage />} />
            <Route path="feedback" element={<FeedbackInboxPage />} />
          </Route>

          {/* Public root: do NOT auto-redirect to /admin (the previous
              behaviour exposed the admin URL on every visit to the apex
              domain). Serve a minimal public landing instead.
              /dashboard kept as a soft alias for authed users only. */}
          <Route path="/dashboard" element={<Navigate to="/admin/overview" replace />} />
          <Route path="/" element={<PublicLanding />} />
          <Route path="*" element={<PublicLanding />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
